import assets from '@wetransfer/transfer/assets';
import {
  checkForPreviews,
  deleteWallpaper,
  preallocateBranded,
  preallocateCurated,
  preallocateCustom,
  processPreviews,
  uploadFile,
} from '@wetransfer/transfer/customWallpaperApi';
import { WallpaperPreviews } from '@wetransfer/transfer/customWallpaperApi/types';
import helper from '@wetransfer/transfer/helper';
import {
  CuratedWallPaper,
  isCuratedWallpaper,
  Wallpaper as WallpaperOld,
} from '@wetransfer/transfer/store/action/helper/image';
import { Wallpaper } from '@wetransfer/transfer/store/customWallpapers';
import { ChannelWallpaper } from '@wetransfer/transfer/store/types';

import { MAX_UPLOAD_FILE_SIZE, UPLOAD_FILE_NAME_REGEX } from './constants';
import { WallpaperCategory } from './types';

export const normalizeWallpaper = (
  wallpaper: WallpaperOld | CuratedWallPaper
): Wallpaper => {
  if (isCuratedWallpaper(wallpaper)) {
    return {
      localId: `curated_${wallpaper.imagePath}`,
      category: WallpaperCategory.CURATED,
      srcLarge: assets.lookup(wallpaper.thumbnailLarge),
      extension: helper.getFileExtension(wallpaper.imagePath),
      fileName: wallpaper.imagePath.split('/').pop(),
      originalAssetUrl: wallpaper.imagePath,
      description: wallpaper.description,
      descrId: wallpaper.imagePath.split('/')[2].split('-')[0],
    };
  }

  return {
    category: WallpaperCategory.BRANDED,
    localId: `branded_${wallpaper.id}`,
    srcLarge: wallpaper.thumbnail_large,
    srcMedium: wallpaper.thumbnail_medium,
    srcSmall: wallpaper.thumbnail_small,
    extension: helper.getFileExtension(wallpaper.source_url),
    fileName: wallpaper.asset_url?.split('/').pop(),
    originalAssetUrl: wallpaper.source_url,
  };
};

export const normalizeChannelWallpaper = (
  wallpaper: ChannelWallpaper
): Wallpaper => {
  return {
    category: WallpaperCategory.BRANDED,
    localId: `branded_${wallpaper.id}`,
    srcLarge: wallpaper.resizedSrcLarge,
    srcMedium: wallpaper.resizedSrcMedium,
    srcSmall: wallpaper.resizedSrcSmall,
    extension: helper.getFileExtension(wallpaper.src),
    fileName: wallpaper.src?.split('/').pop(),
    originalAssetUrl: wallpaper.src,
  };
};

export const getWallpaperSrc = (wallpaper: Wallpaper): string =>
  wallpaper?.srcLarge ||
  wallpaper?.srcMedium ||
  wallpaper?.srcSmall ||
  wallpaper?.localFileSrc;

export const pollUntil = async <T>(
  poll: () => Promise<T | undefined>,
  interval: number,
  maxTries?: number
): Promise<T> => {
  let tries = 0;

  const run = async () => {
    tries++;
    const result = await poll();

    if (result) {
      return result;
    }

    if (typeof maxTries === 'number' && tries >= maxTries) {
      throw new Error(
        `Failed to resolve the Promise within the given number of tries (${maxTries})`
      );
    }

    // Try again
    return new Promise<T | undefined>((resolve) =>
      setTimeout(() => {
        resolve(run());
      }, interval)
    );
  };

  return run();
};

export const getWallpaperPreviews = async (
  assetId: string
): Promise<WallpaperPreviews> => {
  const processPreviewsResponse = await processPreviews(assetId);

  if (processPreviewsResponse !== 201) {
    throw new Error('Error requesting previews');
  }

  const previews = await pollUntil(
    async () => {
      const previews = await checkForPreviews(assetId);

      if (
        previews.resizedSrcLarge &&
        previews.resizedSrcMedium &&
        previews.resizedSrcLarge
      ) {
        return previews;
      }
    },
    400,
    5
  );

  return previews;
};

export const uploadCuratedWallpaper = async (
  imagePath: string
): Promise<{ assetId: string }> => {
  const { assetId } = await preallocateCurated({
    imagePath,
  });

  return {
    assetId,
  };
};

export const uploadBrandedWallpaper = async (
  sourceUrl: string
): Promise<{ assetId: string }> => {
  const { assetId } = await preallocateBranded({
    sourceUrl,
  });

  return { assetId };
};

export const uploadCustomWallpaperFile = async (
  file: File
): Promise<{ assetId: string }> => {
  const { presignedPutUrl, assetId } = await preallocateCustom({
    originalFileName: file.name,
  });

  const uploadStatus = await uploadFile(presignedPutUrl, file);

  if (uploadStatus !== 200) {
    throw new Error('An error occurred when uploading the file');
  }

  return { assetId };
};

export const deleteWallpapers = async (
  assetIds: string[]
): Promise<(string | undefined)[]> =>
  Promise.all(
    assetIds.map((id) =>
      pollUntil(
        async () => {
          try {
            await deleteWallpaper(id);

            return id;
          } catch {}
        },
        1000,
        5
      )
    )
  );

export const validateWallpaperFile = (file: File): boolean => {
  return Boolean(
    file?.name &&
      file.size <= MAX_UPLOAD_FILE_SIZE &&
      UPLOAD_FILE_NAME_REGEX.exec(file.name)
  );
};
